import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Coffee, User, LogOut, Heart, Plus, Menu, X, Mail, Flag, ShieldCheck, BookOpen, BadgeCheck } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-[#C05A46]" : "text-[#2C1E16] hover:text-[#C05A46]"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-[#F9F6F0]/90 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <Coffee className="h-5 w-5 text-[#C05A46]" strokeWidth={1.5} />
          <span className="font-serif text-2xl tracking-tight">BrewFinder</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/directory" className={linkClass} data-testid="nav-directory">Directory</NavLink>
          <NavLink to="/cities" className={linkClass} data-testid="nav-cities">Cities</NavLink>
          <NavLink to="/lists" className={linkClass} data-testid="nav-lists">Lists</NavLink>
          <NavLink to="/submit" className={linkClass} data-testid="nav-submit">Submit a spot</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-sm gap-2" data-testid="user-menu-trigger">
                  <span className="h-7 w-7 rounded-full bg-[#C05A46] text-[#F9F6F0] grid place-items-center text-xs font-medium">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="text-sm">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-sm bg-[#F9F6F0] border-border">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[#786B63]">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" strokeWidth={1.5} /> Profile & Favorites
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/submit")} className="cursor-pointer">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Submit a café
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[#786B63]">
                      Admin
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/newsletters")}
                      data-testid="menu-admin-newsletters"
                      className="cursor-pointer"
                    >
                      <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} /> Newsletters
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/cafes")}
                      data-testid="menu-admin-cafes"
                      className="cursor-pointer"
                    >
                      <ShieldCheck className="h-4 w-4 mr-2" strokeWidth={1.5} /> Moderation queue
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/reports")}
                      data-testid="menu-admin-reports"
                      className="cursor-pointer"
                    >
                      <Flag className="h-4 w-4 mr-2" strokeWidth={1.5} /> Reports
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/claims")}
                      data-testid="menu-admin-claims"
                      className="cursor-pointer"
                    >
                      <BadgeCheck className="h-4 w-4 mr-2" strokeWidth={1.5} /> Ownership claims
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/lists")}
                      data-testid="menu-admin-lists"
                      className="cursor-pointer"
                    >
                      <BookOpen className="h-4 w-4 mr-2" strokeWidth={1.5} /> Lists & guides
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="menu-logout"
                  className="cursor-pointer text-[#C05A46] focus:text-[#C05A46]"
                >
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" asChild className="rounded-sm">
                <Link to="/login" data-testid="nav-signin">Sign in</Link>
              </Button>
              <Button
                asChild
                className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]"
              >
                <Link to="/register" data-testid="nav-signup">Join free</Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-sm hover:bg-[#F2EBE1]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-[#F9F6F0]">
          <div className="px-6 py-4 flex flex-col gap-3">
            <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>Home</NavLink>
            <NavLink to="/directory" className={linkClass} onClick={() => setOpen(false)}>Directory</NavLink>
            <NavLink to="/cities" className={linkClass} onClick={() => setOpen(false)}>Cities</NavLink>
            <NavLink to="/submit" className={linkClass} onClick={() => setOpen(false)}>Submit a spot</NavLink>
            {user ? (
              <>
                <NavLink to="/profile" className={linkClass} onClick={() => setOpen(false)}>Profile</NavLink>
                <button
                  onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                  className="text-left text-sm text-[#C05A46]"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm" onClick={() => setOpen(false)}>Sign in</Link>
                <Link to="/register" className="text-sm text-[#C05A46]" onClick={() => setOpen(false)}>Join free</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
